import { DashboardType } from '@squaredup/dashboards';
import { dashboardQueryKeys } from 'queries/queryKeys/dashboardKeys';
import { dataStreamDefinitionQueryKeys } from 'queries/queryKeys/dataStreamDefinitionKeys';
import { scopeQueryKeys } from 'queries/queryKeys/scopeKeys';
import { workspaceQueryKeys } from 'queries/queryKeys/workspaceKeys';
import { flattenDashboardIdOrder } from 'queries/utils/dashboardSorted';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router';
import { CreateDefaultDashboards } from 'services/PluginService';
import { Workspace } from 'services/WorkspaceService';
import { useSetFolderOpen } from 'ui/nav/components/dashboards/useFoldersOpen';

interface useDefaultDashboardCreationProps {
    configId: string;
    pluginId: string;
    workspaceId: string;
    selectedSampleDashboards?: string[];
}
export const useDefaultDashboardCreation = (navigateOnCreate: boolean = false) => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const setFoldersOpen = useSetFolderOpen();

    return useMutation(
        ({ configId, pluginId, workspaceId, selectedSampleDashboards }: useDefaultDashboardCreationProps) =>
            CreateDefaultDashboards(configId, pluginId, workspaceId, selectedSampleDashboards),
        {
            onSuccess: (dashboards, { workspaceId }) => {
                // Put the dashboard order property into the workspace properties to ensure dashboards are nested
                // when added to the dashboard list query cache
                const folder = dashboards[0]?.rootFolder;
                queryClient.setQueryData<Workspace[] | undefined>(
                    workspaceQueryKeys.list,
                    (spaces) => spaces?.map((space) =>
                        space.id === workspaceId
                            ? ({
                                  ...space,
                                  data: {
                                      ...space.data,
                                      properties: {
                                          ...space.data.properties,
                                          dashboardIdOrder: [...(space.data.properties?.dashboardIdOrder || []), folder]
                                      }
                                  }
                              } as Workspace)
                            : space
                    )
                );

                // Populate the dashboard list query cache to ensure we can navigate to the chosen dashboard immediately
                queryClient.setQueryData<DashboardType[]>(
                    dashboardQueryKeys.list, 
                    (data) => [
                        ...data ?? [], 
                        ...dashboards
                            .filter(({ dashboard }) => Boolean(dashboard))
                            .map(({ dashboard }) => dashboard!)
                    ]
                );

                // Add dashboard to list of dashboards
                queryClient.invalidateQueries(dashboardQueryKeys.all);
                queryClient.invalidateQueries(workspaceQueryKeys.all);
                queryClient.removeQueries(scopeQueryKeys.all);
                queryClient.removeQueries(dataStreamDefinitionQueryKeys.forAllWorkspaces());

                if (navigateOnCreate && dashboards.length) {
                    const ids = flattenDashboardIdOrder(folder?.dashboardIdOrder || []);

                    const firstDashboardId =
                        ids.find(({ id }) => dashboards.some((dash) => dash.dashboard?.id === id))?.id || 
                        dashboards[0].dashboard?.id;

                    navigate(`/dashboard/${firstDashboardId}?force_open_folder=true`);
                } else if (folder) {
                    setFoldersOpen([{ itemId: folder.id, open: true }]);
                }
            }
        }
    );
};
